import { useCallback, useEffect, useRef, useState } from "react";

/**
 * One list the energy page shows. The blocking spinner shows only on the
 * first load; `reload` refreshes in the background after a change, and a
 * response that arrives after a newer request is dropped.
 *
 * @template T
 * @param {() => Promise<{ isSuccess: boolean, data: { data?: T[], error?: string } } | undefined>} fetchList a stable api function
 * @param {string} failure what to show when the list cannot be loaded
 * @returns {{ rows: T[], loading: boolean, error: string, reload: () => Promise<void> }}
 */
export function useEnergyList(fetchList, failure) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const fetchIdRef = useRef(0);

  const reload = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    setError("");
    try {
      const res = await fetchList();
      if (fetchId !== fetchIdRef.current) return;
      const list = res?.isSuccess === false ? null : res?.data?.data;
      if (Array.isArray(list)) setRows(list);
      else setError(res?.data?.error || failure);
    } catch {
      if (fetchId === fetchIdRef.current) setError(failure);
    } finally {
      if (fetchId === fetchIdRef.current) setLoading(false);
    }
  }, [fetchList, failure]);

  useEffect(() => {
    reload();
    return () => {
      fetchIdRef.current += 1;
    };
  }, [reload]);

  return { rows, loading, error, reload };
}
